/**
 * Ein NEUER Punkt wird nach dem Kartierverfahren seines Projekts ausgerichtet.
 *
 * Das Projekt bestimmt, was ein neuer Punkt ist (siehe `verfahren.ts`, Regel 2).
 * Das gilt für frisch angelegte Punkte genauso wie für kopierte: Beide laufen in
 * bpweb über denselben Weg (`DataService.createBohrpunktFrom`), und an genau
 * dieser einen Stelle wird diese Funktion gerufen.
 *
 * Kopieren über Verfahrensgrenzen hinweg: Wird ein KA-Punkt in ein SEA-Projekt
 * kopiert, fällt sein KA-Teil weg, und umgekehrt bekommt die Kopie eines
 * SEA-Punktes in einem KA-Projekt eine leere KA-Aufnahme. Ein Punkt gehört zu
 * genau einem Verfahren; ein Zwitter ließe sich weder prüfen noch drucken.
 */

import { KaAufnahme, leereKaAufnahme } from './ka/ka-modell';
import { KaAuflage, MitVerfahren, kaAuflageVon, verfahrenVon, vermerkFuerNeuenPunkt } from './verfahren';

/** Der Teil eines Punktes, um den es hier geht. */
export interface PunktMitVerfahren extends MitVerfahren {
	ka?: KaAufnahme;
}

export interface AusrichtOptionen {
	/** Beim Kopieren: die Horizontzeilen der Vorlage übernehmen (mit frischen Kennungen). */
	horizonteBehalten?: boolean;
	/** Liefert eine neue Zeilenkennung (`neueZeilenKennung` aus dem Datenmodell). */
	neueKennung: () => string;
}

/**
 * Setzt Vermerk und KA-Teil des Punktes so, wie das Projekt es verlangt. Ändert
 * den übergebenen Punkt und gibt ihn zurück.
 *
 * Wirft mit Klartext, wenn das Projekt ein Verfahren trägt, das diese Fassung der
 * App nicht kennt: Dann wird nicht geraten, sondern nichts angelegt.
 */
export function richtePunktNachProjektAus<T extends PunktMitVerfahren>(
	punkt: T,
	projekt: MitVerfahren | null | undefined,
	optionen: AusrichtOptionen,
): T {
	const vermerk = vermerkFuerNeuenPunkt(projekt);

	if (verfahrenVon(vermerk) === 'sea95') {
		// Ein SEA-Punkt trägt keinen Vermerk und keinen KA-Teil: Er bleibt derselbe
		// Datensatz wie vor der Einführung der Verfahren.
		delete punkt.verfahren;
		delete punkt.kaAuflage;
		delete punkt.ka;
		return punkt;
	}

	const auflage = kaAuflageVon(vermerk) as KaAuflage;
	const vorlage = verfahrenVon(punkt) === 'ka' ? punkt.ka : undefined;

	// Eigene Kopie: Die Vorlage liegt weiter in der Liste und darf sich mit dem
	// neuen Punkt keine Objekte teilen.
	const ka: KaAufnahme = vorlage ? JSON.parse(JSON.stringify(vorlage)) : leereKaAufnahme(auflage);
	ka.auflage = auflage;
	ka.horizonte = optionen.horizonteBehalten
		? (ka.horizonte ?? []).map(zeile => ({ ...zeile, id: optionen.neueKennung() }))
		: [];

	punkt.verfahren = 'ka';
	punkt.kaAuflage = auflage;
	punkt.ka = ka;
	return punkt;
}
